import React, { useState } from 'react';
import { generateMockFollowupAnswers } from '../../../utils/aiMockDataGenerator';
import { Sparkles, RefreshCw, CheckCircle, AlertCircle, MessageSquare, Edit2 } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

const Step6_FollowupQuestionsGenerator = ({ wizardData, updateData }) => {
  const { profile, questionSet, initialAnswers, followupQuestions, followupAnswers, selectedAccount } = wizardData;
  const [generatingQuestions, setGeneratingQuestions] = useState(false);
  const [generatingAnswers, setGeneratingAnswers] = useState(false);
  const [error, setError] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);

  const generateFollowupQuestions = async () => {
    setGeneratingQuestions(true);
    setError(null);

    try {
      console.log('🤖 Generating personalized follow-up questions based on initial answers...');

      // Create prompt with initial questions and answers
      const prompt = `Generate 7 personalized follow-up questions (Questions 9-15) based on the student's initial responses.

**STUDENT PROFILE:**
Name: ${profile.name}
Intended Major: ${profile.intended_major}

**INITIAL QUESTIONS AND ANSWERS:**
${Object.entries(initialAnswers).map(([key, answer], index) => `
Question ${index + 1}: ${questionSet[index]?.question || 'Question'}
Answer: ${answer}
`).join('\n')}

**TASK:**
Generate 7 follow-up questions (numbered 9-15) that:
1. Reference SPECIFIC topics, themes, situations, or experiences mentioned in the student's actual answers
2. Go deeper into patterns you notice across multiple responses
3. Ask for concrete examples, moments, or details about what they described
4. Connect their experiences to their intended major (${profile.intended_major})
5. Explore growth, challenges, collaboration, advice-giving, surprises, and perspective changes

**OUTPUT FORMAT:**
Return ONLY a valid JSON array with this structure:
[
  {
    "question": "You mentioned [specific topic from their answer]. Can you describe...",
    "rationale": "Brief explanation of what this reveals"
  },
  ... (7 questions total)
]

IMPORTANT: Replace ALL placeholders like [topic], [theme], [situation] with ACTUAL SPECIFIC references from their answers!`;

      const response = await fetch('/api/anthropic/mock-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'generate-followup-answers', // Reuse for text generation
          prompt,
          model: 'claude-sonnet-4-20250514',
          max_tokens: 4000
        })
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const result = await response.json();
      const questionsText = result.response;

      // Parse JSON from response
      const jsonMatch = questionsText.match(/\[[\s\S]*\]/);
      if (!jsonMatch) {
        throw new Error('Could not parse questions from AI response');
      }

      const parsedQuestions = JSON.parse(jsonMatch[0]);

      // Convert to proper format with id, type, placeholder, focus fields
      const questions = parsedQuestions.map((q, index) => ({
        id: `q${index + 9}`,
        type: 'text',
        focus: '',
        question: q.question,
        placeholder: 'Share your thoughts...',
        rationale: q.rationale
      }));

      updateData('followupQuestions', questions);
      console.log('✅ Generated personalized follow-up questions');
    } catch (err) {
      console.error('Error generating follow-up questions:', err);
      setError('Failed to generate follow-up questions. Please try again.');
    } finally {
      setGeneratingQuestions(false);
    }
  };

  const generateFollowupAnswersFunc = async () => {
    setGeneratingAnswers(true);
    setError(null);

    try {
      const answers = await generateMockFollowupAnswers(followupQuestions, initialAnswers, profile);
      updateData('followupAnswers', answers);

      // Save to database if we have a selected account
      if (selectedAccount) {
        try {
          const userId = selectedAccount.auth_user_id || selectedAccount.id;

          // Combine all responses
          const allResponses = {
            ...initialAnswers,
            ...answers
          };

          // Check if assessment_responses record exists
          const { data: existingResponse } = await supabase
            .from('assessment_responses')
            .select('id')
            .eq('user_id', userId)
            .single();

          const assessmentData = {
            user_id: userId,
            assessment_type: 'enhanced',
            responses: allResponses,
            initial_questions: questionSet || [],
            dynamic_questions: followupQuestions || [],
            updated_at: new Date().toISOString()
          };

          if (existingResponse) {
            // Update existing record
            await supabase
              .from('assessment_responses')
              .update(assessmentData)
              .eq('user_id', userId);
          } else {
            // Insert new record
            await supabase
              .from('assessment_responses')
              .insert(assessmentData);
          }

          console.log('✅ Saved followup answers to database');
        } catch (dbError) {
          console.error('Error saving to database:', dbError);
          // Don't throw - answers were still generated
        }
      }
    } catch (err) {
      console.error('Error generating follow-up answers:', err);
      setError('Failed to generate mock follow-up answers. Please try again.');
    } finally {
      setGeneratingAnswers(false);
    }
  };

  const updateAnswer = (questionKey, value) => {
    updateData('followupAnswers', {
      ...followupAnswers,
      [questionKey]: value
    });
  };

  const hasQuestions = followupQuestions && followupQuestions.length >= 7;
  const hasAnswers = followupAnswers && Object.keys(followupAnswers).length >= 7;

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-slate-900 mb-2">Follow-up Questions & Answers</h2>
        <p className="text-slate-600">
          Generate follow-up questions (9-15) based on {profile.name}&#39;s initial responses, then create mock answers.
        </p>
      </div>

      {/* Step 1: Generate Follow-up Questions */}
      {!hasQuestions && (
        <div className="bg-gradient-to-br from-rose-50 to-pink-50 border-2 border-rose-300 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 bg-rose-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-slate-900 mb-2">Generate Follow-up Questions</h3>
          <p className="text-slate-600 mb-6 max-w-2xl mx-auto">
            Create personalized follow-up questions based on the student's initial answers.
          </p>
          <button
            onClick={generateFollowupQuestions}
            disabled={generatingQuestions}
            className="px-8 py-4 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors font-semibold text-lg disabled:opacity-50 flex items-center gap-3 mx-auto"
          >
            {generatingQuestions ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Generating Follow-up Questions...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Generate Follow-up Questions
              </>
            )}
          </button>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-semibold text-red-900 mb-1">Generation Failed</h4>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Step 2: Show Questions and Generate Answers */}
      {hasQuestions && !hasAnswers && (
        <div className="space-y-6">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-emerald-900 mb-1">Follow-up Questions Generated!</h4>
              <p className="text-sm text-emerald-700">
                Review the questions below, then generate mock answers. Click "Regenerate" if questions have placeholders.
              </p>
            </div>
            <button
              onClick={generateFollowupQuestions}
              disabled={generatingQuestions}
              className="text-emerald-600 hover:text-emerald-800 flex items-center gap-2 text-sm font-medium disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${generatingQuestions ? 'animate-spin' : ''}`} />
              {generatingQuestions ? 'Regenerating...' : 'Regenerate'}
            </button>
          </div>

          <div className="space-y-4">
            {followupQuestions.map((q, index) => (
              <div key={index} className="bg-white border border-slate-200 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 bg-rose-100 rounded-full flex items-center justify-center text-rose-700 font-bold">
                    {index + 9}
                  </div>
                  <h4 className="font-semibold text-slate-900">Question {index + 9}</h4>
                </div>
                <p className="text-slate-700 leading-relaxed">{q.question}</p>
                <div className="mt-3 bg-slate-50 rounded-lg p-3">
                  <p className="text-xs text-slate-600">
                    <span className="font-medium">Rationale:</span> {q.rationale}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-br from-violet-50 to-purple-50 border-2 border-violet-300 rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Generate Mock Follow-up Answers</h3>
            <p className="text-slate-600 mb-6 max-w-2xl mx-auto">
              AI will create realistic follow-up responses that build on the initial answers.
            </p>
            <button
              onClick={generateFollowupAnswersFunc}
              disabled={generatingAnswers}
              className="px-8 py-4 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors font-semibold text-lg disabled:opacity-50 flex items-center gap-3 mx-auto"
            >
              {generatingAnswers ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Generating Follow-up Answers...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Generate Follow-up Answers
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Show Editable Answers */}
      {hasAnswers && (
        <div className="space-y-6">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-emerald-900 mb-1">Follow-up Answers Generated!</h4>
              <p className="text-sm text-emerald-700">
                Review and edit the follow-up answers below. If questions have placeholders, regenerate questions first.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={generateFollowupQuestions}
                disabled={generatingQuestions}
                className="text-emerald-600 hover:text-emerald-800 flex items-center gap-2 text-sm font-medium disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${generatingQuestions ? 'animate-spin' : ''}`} />
                {generatingQuestions ? 'Regenerating...' : 'Regenerate Questions'}
              </button>
              <span className="text-slate-300">|</span>
              <button
                onClick={generateFollowupAnswersFunc}
                disabled={generatingAnswers}
                className="text-emerald-600 hover:text-emerald-800 flex items-center gap-2 text-sm font-medium disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${generatingAnswers ? 'animate-spin' : ''}`} />
                {generatingAnswers ? 'Regenerating...' : 'Regenerate Answers'}
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {followupQuestions.map((q, index) => (
              <div key={index} className="bg-white border border-slate-200 rounded-lg p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-rose-100 rounded-full flex items-center justify-center text-rose-700 font-bold">
                      {index + 9}
                    </div>
                    <h4 className="font-semibold text-slate-900">Question {index + 9}</h4>
                  </div>
                  <button
                    onClick={() => setEditingIndex(editingIndex === index ? null : index)}
                    className="text-rose-600 hover:text-rose-800 flex items-center gap-2 text-sm"
                  >
                    <Edit2 className="w-4 h-4" />
                    {editingIndex === index ? 'Done' : 'Edit Answer'}
                  </button>
                </div>

                <p className="text-slate-700 mb-4 leading-relaxed bg-slate-50 p-3 rounded-lg">{q.question}</p>

                {editingIndex === index ? (
                  <textarea
                    value={followupAnswers[`q${index + 9}`] || ''}
                    onChange={(e) => updateAnswer(`q${index + 9}`, e.target.value)}
                    rows={6}
                    className="w-full px-3 py-2 border border-rose-300 rounded-lg focus:ring-2 focus:ring-rose-500"
                    placeholder="Student's follow-up answer..."
                  />
                ) : (
                  <div className="bg-rose-50 rounded-lg p-4">
                    <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                      {followupAnswers[`q${index + 9}`] || 'No answer yet'}
                    </p>
                  </div>
                )}

                <div className="mt-2 text-xs text-slate-500">
                  {followupAnswers[`q${index + 9}`]?.length || 0} characters
                </div>
              </div>
            ))}
          </div>

          <div className="bg-azure-50 border border-azure-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-azure-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-azure-900 mb-1">Next Step: Final Assessment</h4>
                <p className="text-sm text-azure-700">
                  All responses will be compiled to generate the final strategic assessment with college recommendations.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Step6_FollowupQuestionsGenerator;
